import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  usePeriodClosure,
  useUpdatePeriodClosure,
  useDeletePeriodClosure,
} from '@/api/periodClosure/periodClosure.api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { CalendarIcon, Lock, Unlock } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useEffect } from 'react';

// Helper to parse date string without timezone conversion
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const periodClosureSchema = z.object({
  closedUntilDate: z.date({
    required_error: 'Seleccione una fecha de cierre',
  }),
  description: z.string().optional(),
});

type PeriodClosureFormValues = z.infer<typeof periodClosureSchema>;

export default function PeriodClosure() {
  const { data: currentClosure, isLoading } = usePeriodClosure();
  const updateClosure = useUpdatePeriodClosure();
  const deleteClosure = useDeletePeriodClosure();

  const form = useForm<PeriodClosureFormValues>({
    resolver: zodResolver(periodClosureSchema),
    defaultValues: {
      closedUntilDate: undefined,
      description: '',
    },
  });

  useEffect(() => {
    if (currentClosure) {
      form.reset({
        closedUntilDate: parseLocalDate(currentClosure.closedUntilDate),
        description: currentClosure.description || '',
      });
    }
  }, [currentClosure, form]);

  const onSubmit = async (data: PeriodClosureFormValues) => {
    try {
      // Format date without timezone conversion
      const year = data.closedUntilDate.getFullYear();
      const month = String(data.closedUntilDate.getMonth() + 1).padStart(2, '0');
      const day = String(data.closedUntilDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      await updateClosure.mutateAsync({
        closedUntilDate: formattedDate,
        description: data.description,
      });
      toast.success('Período cerrado exitosamente', {
        description: `Los asientos hasta el ${format(data.closedUntilDate, 'dd/MM/yyyy')} están bloqueados`,
      });
    } catch (error: any) {
      toast.error('Error al cerrar el período', {
        description: error.response?.data?.message || 'Intente nuevamente',
      });
    }
  };

  const handleOpenPeriod = async () => {
    try {
      await deleteClosure.mutateAsync();
      form.reset({
        closedUntilDate: undefined,
        description: '',
      });
      toast.success('Período abierto exitosamente', {
        description: 'Todos los períodos están ahora disponibles para modificaciones',
      });
    } catch (error: any) {
      toast.error('Error al abrir el período', {
        description: error.response?.data?.message || 'Intente nuevamente',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-10">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-6 h-6" />
            Cierre de Período Contable
          </CardTitle>
          <CardDescription>
            Configure la fecha hasta la cual se bloqueará la fecha contable. Los usuarios no podrán
            crear o modificar comprobantes cuya fecha contable sea igual o anterior a la fecha de
            cierre.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {currentClosure && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Período actualmente cerrado</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Fecha de cierre:{' '}
                    <strong>
                      {format(parseLocalDate(currentClosure.closedUntilDate), 'dd/MM/yyyy', {
                        locale: es,
                      })}
                    </strong>
                  </p>
                  {currentClosure.description && (
                    <p className="text-sm text-amber-700 mt-1">
                      Descripción: {currentClosure.description}
                    </p>
                  )}
                  <p className="text-xs text-amber-600 mt-2">
                    Cerrado por: {currentClosure.createdBy} el{' '}
                    {format(parseLocalDate(currentClosure.createdAt), 'dd/MM/yyyy HH:mm', {
                      locale: es,
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="closedUntilDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Fecha de Cierre</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground',
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'dd/MM/yyyy', { locale: es })
                            ) : (
                              <span>Seleccione una fecha</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          defaultMonth={field.value}
                          mode="single"
                          showOutsideDays
                          fixedWeeks
                          captionLayout="dropdown"
                          fromYear={new Date().getFullYear() - 5}
                          toYear={new Date().getFullYear() + 10}
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={es}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      La fecha contable igual o anterior a esta fecha quedará bloqueada
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descripción (Opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Ej: Cierre mensual - Enero 2026"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Agregue una descripción para identificar este cierre
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={updateClosure.isPending}
                  className="flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  {currentClosure ? 'Actualizar Cierre' : 'Cerrar Período'}
                </Button>
                {currentClosure && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        form.reset({
                          closedUntilDate: parseLocalDate(currentClosure.closedUntilDate),
                          description: currentClosure.description || '',
                        })
                      }
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={deleteClosure.isPending}
                      onClick={handleOpenPeriod}
                      className="flex items-center gap-2 ml-auto"
                    >
                      <Unlock className="w-4 h-4" />
                      Abrir Período
                    </Button>
                  </>
                )}
              </div>
            </form>
          </Form>

          <div className="mt-8 pt-8 border-t">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Unlock className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-medium text-foreground mb-2">Importante:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Solo los administradores pueden modificar el cierre de períodos</li>
                  <li>El cierre afecta la fecha contable de facturas y asientos contables</li>
                  <li>Los usuarios verán bloqueada la fecha contable en esos formularios</li>
                  <li>Puede actualizar la fecha de cierre en cualquier momento</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
